export const NCFStatus = {
  AVAILABLE: 'available',
  USED: 'used',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
} as const;
export type NCFStatus = (typeof NCFStatus)[keyof typeof NCFStatus];

/**
 * DGII NCF / e-CF receipt type codes. Const object + type alias so
 * every reader / writer uses NCFType.B01 etc. instead of magic
 * strings. Order matches DGII catalog.
 */
export const NCFType = {
  B01: 'B01',
  B02: 'B02',
  B03: 'B03',
  B04: 'B04',
  B11: 'B11',
  B12: 'B12',
  B14: 'B14',
  B15: 'B15',
  B16: 'B16',
  E31: 'E31',
  E32: 'E32',
  E33: 'E33',
  E34: 'E34',
  E41: 'E41',
  E42: 'E42',
  E43: 'E43',
  E44: 'E44',
  E45: 'E45',
  E46: 'E46',
  E47: 'E47',
} as const;
export type NCFType = (typeof NCFType)[keyof typeof NCFType];

/**
 * DGII comprobante labels (Spanish, singular source of truth for FE +
 * email + PDF surfaces). Kept without the code suffix — callers that
 * want "(B01)" append it themselves.
 */
export const NCF_COMPROBANTE_LABELS: Record<NCFType, string> = {
  B01: 'Factura de Crédito Fiscal',
  B02: 'Factura de Consumo',
  B03: 'Nota de Débito',
  B04: 'Nota de Crédito',
  B11: 'Comprobante de Compras',
  B12: 'Registro Único de Ingresos',
  B14: 'Comprobante para Regímenes Especiales',
  B15: 'Comprobante Gubernamental',
  B16: 'Comprobante para Exportaciones',
  E31: 'Factura de Crédito Fiscal Electrónica',
  E32: 'Factura de Consumo Electrónica',
  E33: 'Nota de Débito Electrónica',
  E34: 'Nota de Crédito Electrónica',
  E41: 'Comprobante de Compras Electrónico',
  E42: 'Registro Único de Ingresos Electrónico',
  E43: 'Comprobante para Gastos Menores Electrónico',
  E44: 'Comprobante de Regímenes Especiales Electrónico',
  E45: 'Comprobante Gubernamental Electrónico',
  E46: 'Comprobante para Exportaciones Electrónico',
  E47: 'Comprobante para Pagos al Exterior Electrónico',
};

/**
 * Runtime list of every valid NCF type code. Derived from the label
 * map so it can never drift from the `NCFType` union. Use for
 * class-validator `@IsIn(...)` allowlists and FE dropdowns.
 */
export const NCF_TYPES = Object.keys(NCF_COMPROBANTE_LABELS) as NCFType[];

/**
 * Resolve the label from an NCF value (full sequence or bare prefix).
 * Returns null when the value doesn't map to a known type.
 */
export function ncfComprobanteLabel(
  ncf: string | null | undefined
): string | null {
  if (!ncf) return null;
  const prefix = ncf.slice(0, 3).toUpperCase() as NCFType;
  return NCF_COMPROBANTE_LABELS[prefix] ?? null;
}

export interface NCF {
  id: string;
  businessId: string;
  /** Owning branch for a per-branch NCF range (per-branch-ncf-ranges).
   *  null = the business-wide default range shared by every branch. */
  branchOfficeId?: string | null;
  /** Transfer provenance set when the owning branch was hard-deleted and
   *  its range moved to the receiving branch. */
  originBranchOfficeId?: string | null;
  ncfType: NCFType;
  ncfNumber: string;
  isElectronic: string;
  serie: string;
  prefix?: string;
  sequence: number;
  finalSequence: number;
  actualSequence: number;
  expirationDate: string;
  usedAt?: string;
  registryDate?: string;
  invoiceId?: string;
  notes?: string;
  /** DGII-issued authorization number (NUM_AUTOR). Unique per business
   *  (partial unique index on LOWER(authorization_number)). */
  authorizationNumber?: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
  deletedAt?: string;
  deletedBy?: string;
  ncfStatus?: NCFStatus;
}

export interface CreateNCFRequest {
  ncfType: NCFType;
  serie: string;
  prefix?: string;
  sequence: number;
  finalSequence: number;
  actualSequence?: number;
  expirationDate: string;
  purchaseDate?: string;
  authorizationNumber?: string;
  notes?: string;
}

export interface UpdateNCFRequest {
  businessId?: string;
  ncfType?: NCFType;
  prefix?: string;
  sequence?: number;
  ncfStatus?: NCFStatus;
  expirationDate?: string;
  registryDate?: string;
  notes?: string;
  usedAt?: string;
  invoiceId?: string;
}

export interface NCFQueryParams {
  businessId?: string;
  ncfType?: NCFType;
  ncfStatus?: NCFStatus;
  search?: string;
  page?: number;
  limit?: number;
}
